import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { TypingTest, TestConfig, TypingStats, Mistake } from '../types';

interface GameState {
  currentTest: TypingTest | null;
  isActive: boolean;
  isPaused: boolean;
  startTime: number | null;
  endTime: number | null;
  currentWordIndex: number;
  currentCharIndex: number;
  userInput: string;
  mistakes: Mistake[];
  stats: TypingStats;
}

type GameAction =
  | { type: 'START_TEST'; payload: { config: TestConfig; text: string; words: string[] } }
  | { type: 'UPDATE_INPUT'; payload: string }
  | { type: 'NEXT_WORD' }
  | { type: 'PREV_WORD' }
  | { type: 'ADD_MISTAKE'; payload: Mistake }
  | { type: 'UPDATE_STATS'; payload: Partial<TypingStats> }
  | { type: 'COMPLETE_TEST' }
  | { type: 'PAUSE_TEST' }
  | { type: 'RESUME_TEST' }
  | { type: 'RESET_TEST' };

const initialStats: TypingStats = {
  wpm: 0,
  accuracy: 0,
  rawWpm: 0,
  correctChars: 0,
  incorrectChars: 0,
  totalChars: 0,
  timeElapsed: 0,
  consistency: 0,
};

const initialState: GameState = {
  currentTest: null,
  isActive: false,
  isPaused: false,
  startTime: null,
  endTime: null,
  currentWordIndex: 0,
  currentCharIndex: 0,
  userInput: '',
  mistakes: [],
  stats: initialStats,
};

const calculateStats = (
  userInput: string,
  correctText: string,
  timeElapsed: number,
  mistakes: Mistake[]
): TypingStats => {
  const totalChars = userInput.length;
  const correctChars = totalChars - mistakes.length;
  const incorrectChars = mistakes.length;
  
  const accuracy = totalChars > 0 ? (correctChars / totalChars) * 100 : 0;
  const timeInMinutes = timeElapsed / 60000; // Convert ms to minutes
  const wpm = timeInMinutes > 0 ? (correctChars / 5) / timeInMinutes : 0;
  const rawWpm = timeInMinutes > 0 ? (totalChars / 5) / timeInMinutes : 0;
  
  return {
    wpm: Math.round(wpm),
    accuracy: Math.round(accuracy * 100) / 100,
    rawWpm: Math.round(rawWpm),
    correctChars,
    incorrectChars,
    totalChars,
    timeElapsed,
    consistency: 0, // Would need more complex calculation
  };
};

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'START_TEST':
      return {
        ...state,
        currentTest: {
          id: Date.now().toString(),
          config: action.payload.config,
          text: action.payload.text,
          words: action.payload.words,
          startTime: Date.now(),
          userInput: '',
          currentWordIndex: 0,
          currentCharIndex: 0,
          stats: initialStats,
          isComplete: false,
          mistakes: [],
        },
        isActive: true,
        isPaused: false,
        startTime: Date.now(),
        endTime: null,
        currentWordIndex: 0,
        currentCharIndex: 0,
        userInput: '',
        mistakes: [],
        stats: initialStats,
      };

    case 'UPDATE_INPUT':
      if (!state.currentTest || !state.isActive || state.isPaused) return state;
      
      const newInput = action.payload;
      const timeElapsed = Date.now() - (state.startTime || 0);
      const newStats = calculateStats(
        newInput,
        state.currentTest.text,
        timeElapsed,
        state.mistakes
      );

      return {
        ...state,
        userInput: newInput,
        stats: newStats,
        currentTest: {
          ...state.currentTest,
          userInput: newInput,
          stats: newStats,
        },
      };

    case 'ADD_MISTAKE':
      return {
        ...state,
        mistakes: [...state.mistakes, action.payload],
        currentTest: state.currentTest ? {
          ...state.currentTest,
          mistakes: [...state.mistakes, action.payload],
        } : null,
      };

    case 'COMPLETE_TEST':
      return {
        ...state,
        isActive: false,
        endTime: Date.now(),
        currentTest: state.currentTest ? {
          ...state.currentTest,
          isComplete: true,
          endTime: Date.now(),
        } : null,
      };

    case 'PAUSE_TEST':
      return {
        ...state,
        isPaused: true,
      };

    case 'RESUME_TEST':
      return {
        ...state,
        isPaused: false,
      };

    case 'RESET_TEST':
      return initialState;

    default:
      return state;
  }
};

interface GameContextType {
  state: GameState;
  startTest: (config: TestConfig, text: string, words: string[]) => void;
  updateInput: (input: string) => void;
  addMistake: (mistake: Mistake) => void;
  completeTest: () => void;
  pauseTest: () => void;
  resumeTest: () => void;
  resetTest: () => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  const startTest = (config: TestConfig, text: string, words: string[]) => {
    dispatch({ type: 'START_TEST', payload: { config, text, words } });
  };

  const updateInput = (input: string) => {
    dispatch({ type: 'UPDATE_INPUT', payload: input });
  };

  const addMistake = (mistake: Mistake) => {
    dispatch({ type: 'ADD_MISTAKE', payload: mistake });
  };

  const completeTest = () => {
    dispatch({ type: 'COMPLETE_TEST' });
  };

  const pauseTest = () => {
    dispatch({ type: 'PAUSE_TEST' });
  };

  const resumeTest = () => {
    dispatch({ type: 'RESUME_TEST' });
  };

  const resetTest = () => {
    dispatch({ type: 'RESET_TEST' });
  };

  const value: GameContextType = {
    state,
    startTest,
    updateInput,
    addMistake,
    completeTest,
    pauseTest,
    resumeTest,
    resetTest,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
